import * as XLSX from 'xlsx';
import type { ScoutingViewRow } from './scoutingView';
import { scoutingStatus } from './scoutingView';
import { BRAND_REVIEW_LABELS, scoutingName, scoutingProductLabels, safeScoutingUrl } from './scoutingDetails';

export type ScoutingExportScope = 'finalistsAndSelected' | 'finalists' | 'selected';
export type ScoutingExportAudience = 'internal' | 'brand';
type Cell = string | number | null;
export const SCOUTING_EXPORT_SCOPES: Record<ScoutingExportScope, string> = {
  finalistsAndSelected: 'Finalists + Selected',
  finalists: 'Finalists',
  selected: 'Selected',
};
export function scoutingExportRows(rows: ScoutingViewRow[], scope: ScoutingExportScope): ScoutingViewRow[] {
  return rows.filter((row) => {
    if (row.rejected || row.decision === 'Declined') {
      return false;
    }
    const status = scoutingStatus(row);
    return (scope !== 'selected' && status === 'Finalist') || (scope !== 'finalists' && status === 'Selected');
  });
}
const BRAND_HEADERS = [
  'Product',
  'Full name',
  'Country',
  'Instagram handle',
  'Instagram link',
  'TikTok handle',
  'TikTok link',
  'Followers',
  'Avg views (stored profile; sample period unavailable)',
  'Last posting',
];
function brandCells(row: ScoutingViewRow): Cell[] {
  const details = row.details;
  const instagram = details?.accounts.find((account) => account.platform.toLowerCase() === 'instagram');
  const tiktok = details?.accounts.find((account) => account.platform.toLowerCase() === 'tiktok');
  return [
    scoutingProductLabels(details?.products).join('; '),
    scoutingName(row),
    details?.contact.country ?? '',
    instagram?.handle ?? '',
    safeScoutingUrl(instagram?.profileUrl) ?? '',
    tiktok?.handle ?? '',
    safeScoutingUrl(tiktok?.profileUrl) ?? '',
    details?.social.followers == null
      ? null
      : `${details.social.platform ?? 'Unknown platform'}: ${details.social.followers}`,
    details?.social.profileAverageViews ?? null,
    details?.social.lastPostedAt ?? '',
  ];
}
const INTERNAL_HEADERS = [
  ...BRAND_HEADERS,
  'Influencer ID',
  'Joined campaign (enrollment timestamp)',
  'Creator score / 10',
  'Score review',
  'Score freshness',
  'Score calculated at',
  'Score formula',
  'Score data through',
  'Score revision',
  'Median views (last 10–12 unpinned posts)',
  'ER (same unpinned sample)',
  'Posts in 30 days',
  'Language',
  'Email',
  'Phone',
  'Address 1',
  'Address 2',
  'City',
  'State',
  'Postcode',
  'Contact source',
  'Scouting status',
  'Decision',
  'Internal decline reason',
  'Internal notes',
  'Brand review (pipeline)',
  'Social observed at',
  'Social coverage',
  'Social coverage reason',
  'Metric platform',
  'Profile average views (not median)',
  'Stored profile ER (%, legacy follower formula)',
];
function internalCells(row: ScoutingViewRow): Cell[] {
  const d = row.details;
  const c = d?.contact;
  return [
    ...brandCells(row),
    row.influencerId,
    d?.appliedAt ?? '',
    d?.score?.finalScore ?? null,
    d?.score?.reviewStatus ?? '',
    d?.score?.freshness ?? '',
    d?.score?.calculatedAt ?? '',
    d?.score?.formulaVersion ?? '',
    d?.score?.inputCutoff ?? '',
    d?.score?.revision ?? null,
    d?.social.medianViews ?? null,
    d?.social.engagementRate ?? null,
    d?.social.posts30Days ?? null,
    d?.social.languages.join('; ') ?? '',
    c?.email ?? '',
    c?.phone ?? '',
    c?.address1 ?? '',
    c?.address2 ?? '',
    c?.city ?? '',
    c?.state ?? '',
    c?.postcode ?? '',
    c?.source ?? 'unavailable',
    scoutingStatus(row),
    row.decision ?? '',
    row.declineReason ?? '',
    row.notes?.text ?? '',
    d ? BRAND_REVIEW_LABELS[d.brandReview.status] : '',
    d?.social.observedAt ?? '',
    d?.social.status ?? 'unavailable',
    d?.social.reason ?? 'Details unavailable',
    d?.social.platform ?? '',
    d?.social.profileAverageViews ?? null,
    d?.social.profileEngagementRate ?? null,
  ];
}
/** Brand output is constructed from its own allowlist, never by removing private fields. */
export function buildScoutingWorkbook(
  rows: ScoutingViewRow[],
  audience: ScoutingExportAudience,
  scope: ScoutingExportScope,
): XLSX.WorkBook {
  const selected = scoutingExportRows(rows, scope);
  const data = [
    audience === 'brand' ? BRAND_HEADERS : INTERNAL_HEADERS,
    ...selected.map(audience === 'brand' ? brandCells : internalCells),
  ];
  const sheet = XLSX.utils.aoa_to_sheet(data);
  data.forEach((row, r) =>
    row.forEach((value, c) => {
      if (typeof value !== 'string') {
        return;
      }
      // Text cells preserve postal leading zeros, phone plus signs, and literal formula-looking input.
      sheet[XLSX.utils.encode_cell({ r, c })] = { t: 's', v: value, z: '@' };
    }),
  );
  sheet['!cols'] = data[0]?.map(() => ({ wch: 22 })) ?? [];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, audience === 'brand' ? 'Brand final list' : 'Internal final list');
  return workbook;
}
export function downloadScoutingWorkbook(
  rows: ScoutingViewRow[],
  audience: ScoutingExportAudience,
  scope: ScoutingExportScope,
): void {
  XLSX.writeFile(
    buildScoutingWorkbook(rows, audience, scope),
    `scouting-${audience}-${scope}-${new Date().toISOString().slice(0, 10)}.xlsx`,
  );
}
